import { EventConstants } from "~/assets/constants/ecomApi";

type Event =
  | "click"
  | "page_view"
  | "form_submit"
  | "form_start"
  | "error"
  | "Sign In"
  | "Sign Up"
  | "PageLoaded"
  | "scroll"
  | "input";

interface GenericEventOptions {
  ecommerce?: any;
  eventName: string;
  eventProperties: any;
  eventAction: Event;
  eventLabel: string;
  eventInteraction: boolean;
}

type EventOptions = {
  options?: GenericEventOptions;
};
type QueuedEvent = {
  eventParameters: EventOptions;
  skipDataDog: boolean;
};

const eventQueue: QueuedEvent[] = [];
let hasConsentProcessed = false;

export const logEvent = (
  eventParameters: EventOptions,
  skipDataDog = false
): void => {

  // Store the events until consent is received
  if (!hasConsentProcessed) {
    eventQueue.push({ eventParameters, skipDataDog });
    return;
  }

    trackEvent(eventParameters, skipDataDog);
};

/**
 * For processing events that are queued up and held due to consent choice
 */
export function processQueuedEvents() {
  hasConsentProcessed = true;

  // Track the queued events in correct order
  while (eventQueue.length > 0) {
    const { eventParameters, skipDataDog } = eventQueue.shift()!;
    trackEvent(eventParameters, skipDataDog);
  }
}


function trackEvent(eventParameters: EventOptions, skipDataDog: boolean) {
  if (import.meta.client && !skipDataDog) {
    logDatadogAction(
        eventParameters.options?.eventName,
        eventParameters.options?.ecommerce ?? eventParameters.options?.eventProperties
    );
  }

  const gtm = useGtm();
  const isGtmEnabled = gtm?.options.enabled;
  if (!isGtmEnabled || !eventParameters.options) return;

  const {
    eventName,
    eventProperties,
    eventAction,
    eventLabel,
    eventInteraction,
    ecommerce,
  } = eventParameters.options!;

  let { ...val } = eventProperties;

  const eventPayload: Record<string, any> = {
    event: eventName ?? "click-default",
    action: eventAction ?? "click-default",
    label: eventLabel ?? "gtm-event",
    noninteraction: eventInteraction ?? false,
    ...val,
  };

  if (ecommerce) {
    eventPayload.ecommerce = ecommerce;
    eventPayload.value = ecommerce.value;
  }

  if (
    eventName === "virtual_pageview" ||
    eventName === "account_logout" ||
    ecommerce
  ) {
    gtm.trackEvent({
      event: "reset",
      category: null,
      action: null,
      label: null,
      noninteraction: false,
      ecommerce: null,
    });
  }

  gtm.trackEvent(eventPayload);


}

export function logDatadogAction(name,options) {

  if (import.meta.client) {
    useNuxtApp().$ddRum?.addAction(
        name.replace(/_/g, ' ').replace(/^./, char => char.toUpperCase()),
        options
    );
  }

}

function buildNavigationTrackObject(navigationJourney: Array<any>) {
  return {
    options: {
      eventAction: "page_view",
      eventProperties: {
        navigationJourney,
      },
      eventInteraction: true,
      eventLabel: `User Navigation: ${JSON.stringify(navigationJourney)}`,
      eventName: "navigationTracking",
    },
  };
}

function buildPageLoadEventOptions(params: {
  user: any;
  toPath: string;
  fromPath: string;
  locale: string;
  isIncVat: boolean;
}) {
  if (!import.meta.client) return null;
  const { user, fromPath, toPath, locale, isIncVat } = params;
  return {
    options: {
      eventAction: "page_view",
      eventProperties: {
        customer: user
          ? {
              user_id: user.id,
              email: user.email,
              phone: user.mobile,
              order_count: user.order_count,
              order_value: user.order_value,
              account_type: user.account_type,
            }
          : "User Is Not Logged In",
        page: {
          page_url: window.location.href,
          page_path: toPath,
          page_referrer: fromPath || document.referrer,
          page_title: document.title,
          content_group: "",
          content_group2: "",
        },
        site: {
          shop_language: locale,
          shop_currency: EventConstants.currency,
          tax_toggle: isIncVat ? "Incl." : "Excl.",
          selected_branch: user?.default_branch || null,
        },
      },
      eventInteraction: true,
      eventLabel: `Page Loaded ${toPath}`,
      eventName: "virtual_pageview",
    },
  };
}

export function logPageView(params: {
  user: any;
  toPath: string;
  fromPath: string;
  locale: string;
  isIncVat: boolean;
}) {
  const pageLoadedEvent = buildPageLoadEventOptions(params);

  logEvent(pageLoadedEvent, true);

  // Update navigation journey
  const navigationJourney = [{ from: params.fromPath, to: params.toPath }];

  // Logging the navigation tracking event
  const navigationTrackingEvent = buildNavigationTrackObject(navigationJourney);

  logEvent(navigationTrackingEvent, true);
}
